import { createClient } from '@supabase/supabase-js';
import {
  createBoundedFetch,
  createMeetingImportClient,
  MeetingRpcError,
  validateMeetingPeopleMatches,
  meetingFileAdmission,
  meetingStoragePath,
  validateMeetingSource,
  type MeetingSource,
} from '@cerebro-diem/core';
import { getSession, supabase } from '../../services/supabase';
import { invokeEdgeFunction } from '../../services/edgeFunctions';
import { meetingStorage } from '../../services/meetingStorage';
async function sessionFor(owner: string) {
  const {
    data: { session },
  } = await getSession();
  if (!session || session.user.id !== owner)
    throw new Error(
      'The signed-in account changed; sign in as the meeting source owner',
    );
  return session;
}
export async function findMeetingPeople(owner: string, names: string[]) {
  const session = await sessionFor(owner);
  const { data, error } = await supabase
    .rpc('find_meeting_people', { p_names: names })
    .setHeader('Authorization', `Bearer ${session.access_token}`);
  if (error) throw error;
  await sessionFor(owner);
  return validateMeetingPeopleMatches(data, owner, names);
}
export const meetingClient = createMeetingImportClient({
  storage: meetingStorage,
  assertOwner: async (owner) => {
    await sessionFor(owner);
  },
  invoke: (body, owner) => invokeEdgeFunction('import-meeting', body, owner),
  rpc: async (args, owner) => {
    const session = await sessionFor(owner);
    const { data, error, status } = await supabase
      .rpc('commit_meeting_import', args)
      .setHeader('Authorization', `Bearer ${session.access_token}`);
    if (error) throw new MeetingRpcError({ ...error, status });
    return data;
  },
  upload: async (source, owner, id) => {
    const session = await sessionFor(owner);
    // A private Storage client has a fixed token for both upload and receipt reconciliation, even during an account switch.
    const storage = createClient(
      import.meta.env.VITE_SUPABASE_URL,
      import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      {
        accessToken: async () => session.access_token,
        global: { fetch: createBoundedFetch(() => window.fetch.bind(window)) },
      },
    ).storage.from('meeting-sources');
    const blob = await meetingStorage.readFile(owner, id);
    if (blob.size !== source.size)
      throw new Error('Retained recording size changed; original file is kept');
    const transportPath = meetingStoragePath(source.path);
    const { data, error } = await storage.upload(
      transportPath,
      new Blob([blob], { type: source.mimeType }),
      { contentType: source.mimeType, upsert: false },
    );
    if (
      error &&
      (!('statusCode' in error) || String(error.statusCode) !== '409')
    )
      throw error;
    if (
      !error &&
      (data?.path !== transportPath ||
        data.fullPath !== `meeting-sources/${source.path}`)
    )
      throw new Error('Recording upload receipt did not match');
    const { data: info, error: infoError } = await storage.info(transportPath);
    if (infoError) throw infoError;
    if (
      !info ||
      info.name !== source.path ||
      info.size !== source.size ||
      info.contentType !== source.mimeType
    )
      throw new Error(
        'Recording upload metadata did not match; local source is kept',
      );
    // Keep the local file until the durable uploadPending flag is cleared. Later device cleanup is harmless.
  },
});
export const {
  createMeetingImport,
  getMeetingImport,
  listMeetingImports,
  processMeetingImport,
  updateMeetingReview,
  commitMeetingImport,
  pendingMeetingImports,
} = meetingClient;
export async function prepareMeetingFile(
  owner: string,
  id: string,
  file: File,
): Promise<MeetingSource> {
  await sessionFor(owner);
  const admitted = meetingFileAdmission(file.name, file.type, file.size);
  if (admitted.kind === 'transcript')
    return validateMeetingSource(
      { kind: 'text', text: await file.text(), name: file.name },
      owner,
      id,
    );
  const source = validateMeetingSource(
    {
      kind: 'media',
      path: `${owner}/${id}/${file.name}`,
      name: file.name,
      size: file.size,
      mimeType: admitted.mimeType,
    },
    owner,
    id,
  );
  await meetingStorage.keepFile(owner, id, file);
  return source;
}
